import {
  DatePicker,
  Flex,
  NumberInput,
  NumberInputProps,
  Select,
  Text,
  TextInput,
  TextInputProps,
  createDatePickerValidator,
  createForm,
} from '@applyboard/crystal-ui'
import {
  FileData,
  HasLanguageProficiencyCodes,
  LanguageProficiency,
  LanguageProficiencyData,
  TestType,
} from '@applyboard/school-applications-types-lib'
import { find, includes, isEmpty, set } from 'lodash'
import { nanoid } from 'nanoid'
import { ComponentType, useEffect, useState } from 'react'
import { SetAllFieldsOptional } from 'schools-domain-backend-utils'
import { Loading } from '../../Loading'

import { RawApplicationResponse, useUpdateApplication } from '../../../hooks'

import { ChangeLanguageTestDialog } from '../ChangeLanguageTestDialog'
import { useApplicationFormContext } from './ApplicationForm'
import { FileUploadField, FileUploadFieldValue } from './FileUploadField'

import {
  GenericError,
  IntegratedSubscores,
  Subscores,
  getLanguageProficiencyFieldsInfo,
  getSubscoresOrder,
  validateSubscore,
} from '../../../utils'
import { convertTimelessDateStrToLocalDate } from '../../../utils/convertTimelessDateStrToLocalDate'
import { DocumentTags, LanguageExamAvailability } from '../../../utils/enums'
import { Asterisk } from '../../Asterisk'
import { StudentApplication } from '../types'
import { getFilesOfTypeLanguageProficiency } from './utils'
import { useGetLanguageProficiencyTypes } from '../../../hooks/useGetLanguageProficiencyTypes'

type LanguageProficiencyFormFields = {
  hasLanguageProficiency: string
  id: string
  testType: string
  certificateNumber: string
  testDate: string
  overallScore: string
  documents: FileUploadFieldValue[]
  listening: string
  reading: string
  writing: string
  speaking: string
  description: string
  production: string
  literacy: string
  comprehension: string
  conversation: string
}

const { Form, Field, useFieldValues, useResetForm, useSetFieldValues } =
  createForm<LanguageProficiencyFormFields>()

type LanguageProficiencyTabProps = {
  disabled?: boolean
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: GenericError) => void
  updateApplication: ReturnType<typeof useUpdateApplication>['updateApplication']
  formId: string
}

const UPLOAD_LIMIT = 1

const defaultValue = {
  testType: '',
  certificateNumber: '',
  testDate: '',
  overallScore: '',
  documents: [],
  listening: '',
  reading: '',
  writing: '',
  speaking: '',
  description: '',
  production: '',
  literacy: '',
  comprehension: '',
  conversation: '',
}

export function LanguageProficiencyTab(props: LanguageProficiencyTabProps) {
  const { resetFiles, pendingFileUploadState } = useApplicationFormContext()

  useEffect(() => {
    const filesPerType = getFilesOfTypeLanguageProficiency(
      props.application?.attributes?.files as FileData,
    )

    const activeFiles = Object.entries(filesPerType).reduce((acc, [id, file]) => {
      if (file?.activeRecord) {
        acc[id] = file
      }

      return acc
    }, {} as FileData)

    resetFiles(activeFiles)
  }, [props.application?.attributes?.files, resetFiles])

  return (
    <Form
      id={props.formId}
      defaultValues={setDefaultLanguageProficiency({
        languageProficiency: props.application?.attributes?.languageProficiency,
        files: getFilesOfTypeLanguageProficiency(props.application?.attributes?.files as FileData),
      })}
      validationMode={'onSubmit'}
      onSubmit={data => {
        if (props.disabled) return props.onSuccess()

        const languageProficiency = {
          hasLanguageProficiency: data.hasLanguageProficiency as HasLanguageProficiencyCodes,
          languageProficiencyData: {} as LanguageProficiencyData,
        }

        if (data.hasLanguageProficiency !== LanguageExamAvailability.HAVE) {
          set(languageProficiency, 'languageProficiencyData', null)
        } else if ((data.testType as TestType) === 'OTHER') {
          languageProficiency.languageProficiencyData[data.id] = {
            testType: data.testType as TestType,
            description: data.description,
          }
        } else if (includes(['CAEL', 'MET', 'CAMBRIDGE', 'EPP'], data.testType)) {
          languageProficiency.languageProficiencyData[data.id] = {
            testType: data.testType as TestType,
          }
        } else if ((data.testType as TestType) === 'DUOLINGO') {
          const subscores = {
            listening: parseFloat(data.listening),
            reading: parseFloat(data.reading),
            writing: parseFloat(data.writing),
            speaking: parseFloat(data.speaking),
          }
          languageProficiency.languageProficiencyData[data.id] = {
            testType: data.testType as TestType,
            certificateNumber: data.certificateNumber,
            testDate: data.testDate.substring(0, 10),
            overallScore: data.overallScore,
            subscores: data.listening ? subscores : undefined,
            integratedSubscores: {
              production: parseFloat(data.production),
              literacy: parseFloat(data.literacy),
              comprehension: parseFloat(data.comprehension),
              conversation: parseFloat(data.conversation),
            },
          }
        } else if (getLanguageProficiencyFieldsInfo(data.testType)) {
          const examData = {
            testType: data.testType as TestType,
            certificateNumber: data.certificateNumber,
            testDate: data.testDate.substring(0, 10),
            overallScore: data.overallScore,
            subscores: {
              listening: parseFloat(data.listening),
              reading: parseFloat(data.reading),
              writing: parseFloat(data.writing),
              speaking: parseFloat(data.speaking),
            },
          }
          languageProficiency.languageProficiencyData[data.id] = examData
        }

        props.updateApplication(
          {
            attributes: {
              languageProficiency,
            },
            files: pendingFileUploadState,
          },
          {
            onSuccess: response => {
              resetFiles(
                getFilesOfTypeLanguageProficiency(response.data?.attributes?.files as FileData),
              )
              props.onSuccess(response)
            },
            onError: props.onError,
          },
        )
      }}
    >
      <LanguageProficiencyItems disabled={props.disabled} application={props.application} />
    </Form>
  )
}

type LanguageProficiencyItemsProps = {
  disabled?: boolean
  application: StudentApplication
}

function LanguageProficiencyItems(props: LanguageProficiencyItemsProps) {
  const { addPendingDelete, getObservableFiles, resetFiles } = useApplicationFormContext()

  const [dialogData, setDialogData] = useState<{
    currentTestValue: string
    isOpen: boolean
    newTestValue: string | null
  }>({
    currentTestValue: '',
    isOpen: false, // is the dialog open or closed
    newTestValue: null,
  })

  const {
    certificateNumber,
    documents,
    hasLanguageProficiency,
    id,
    listening,
    overallScore,
    reading,
    speaking,
    testDate,
    testType,
    writing,
    production,
    literacy,
    comprehension,
    conversation,
    description,
  } = useFieldValues([
    'certificateNumber',
    'documents',
    'hasLanguageProficiency',
    'id',
    'listening',
    'overallScore',
    'reading',
    'speaking',
    'testDate',
    'testType',
    'writing',
    'production',
    'literacy',
    'comprehension',
    'conversation',
    'description',
  ])
  const setFieldValues = useSetFieldValues()
  const resetForm = useResetForm()

  const testTypeOptions = useGetLanguageProficiencyTypes({
    files: getFilesOfTypeLanguageProficiency(props.application.attributes?.files as FileData),
  })

  if (!id || !testTypeOptions) {
    return <Loading />
  }

  return (
    <>
      <Flex direction="column" gap={4}>
        <Text>
          Please provide information about your language proficiency as per the program
          requirements.
        </Text>
        <Flex direction="column" gap={4}>
          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis="100%">
              <Field
                as={Select}
                label="Will you provide language proficiency proof now?"
                name={`hasLanguageProficiency`}
                appearance="styled"
                disabled={props.disabled}
                required="This field is required"
                onChange={value => {
                  // this works due to us only supporting 1 test in this form
                  resetFiles(
                    getFilesOfTypeLanguageProficiency(
                      props.application?.attributes?.files as FileData,
                    ),
                  )

                  setFieldValues({
                    hasLanguageProficiency: value as string,
                    id: id,
                    ...defaultValue,
                  })
                }}
              >
                <Select.Option value="HAVE" label="Yes, and I can provide this now" />
                <Select.Option value="WILL_HAVE" label="Yes, but I will provide this later" />
                <Select.Option value="DONT_NEED" label="No, I don’t need to provide this" />
              </Field>
            </Flex.Item>
            {hasLanguageProficiency === LanguageExamAvailability.HAVE ? (
              <>
                <Flex.Item basis="100%">
                  <Field
                    as={Select}
                    label="Proof type"
                    name={`testType`}
                    appearance="styled"
                    disabled={props.disabled}
                    required={
                      isFieldRequired({
                        testType,
                        hasLanguageProficiency: hasLanguageProficiency,
                        disabled: props.disabled,
                      })
                        ? 'Proof type is required'
                        : false
                    }
                    onChange={value => {
                      const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)

                      if (
                        fieldsInfo?.fileType &&
                        testType.length !== 0 &&
                        (!isEmpty(testDate) ||
                          !isEmpty(certificateNumber) ||
                          !isEmpty(documents) ||
                          !isEmpty(
                            getObservableFiles({
                              fileType: fieldsInfo.fileType,
                              sectionReference: id,
                            }),
                          ) ||
                          !isEmpty(overallScore) ||
                          !isEmpty(listening) ||
                          !isEmpty(reading) ||
                          !isEmpty(speaking) ||
                          !isEmpty(writing) ||
                          !isEmpty(production) ||
                          !isEmpty(literacy) ||
                          !isEmpty(comprehension) ||
                          !isEmpty(conversation) ||
                          !isEmpty(description))
                      ) {
                        setDialogData({
                          currentTestValue: testType,
                          isOpen: true,
                          newTestValue: (value as string | undefined) || null,
                        })
                      }
                    }}
                  >
                    {Object.entries(testTypeOptions).map(([value, label]) => (
                      <Select.Option value={value} label={label} key={value} />
                    ))}
                  </Field>
                </Flex.Item>
                <SelectedTypeFields disabled={props.disabled} application={props.application} />
                <SelectedOtherTypeFields disabled={props.disabled} />
                <SelectedDuolingoTypeFields disabled={props.disabled} />
                <UploadField disabled={props.disabled} application={props.application} />
              </>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
      <ChangeLanguageTestDialog
        currentTestLabel={
          testTypeOptions[dialogData.currentTestValue as keyof typeof testTypeOptions]
        }
        isOpen={dialogData.isOpen}
        newTestLabel={
          dialogData.newTestValue
            ? testTypeOptions[dialogData.newTestValue as keyof typeof testTypeOptions]
            : ''
        }
        onCloseDialog={(isOpen: boolean) => {
          setFieldValues({
            testType: dialogData.currentTestValue,
          })

          setDialogData({
            currentTestValue: '',
            isOpen: isOpen,
            newTestValue: null,
          })
        }}
        onConfirm={() => {
          const fieldsInfo = getLanguageProficiencyFieldsInfo(dialogData.currentTestValue)
          Object.keys(
            getObservableFiles({ fileType: fieldsInfo?.fileType, sectionReference: id }),
          ).forEach(id => {
            addPendingDelete(id)
          })

          resetForm()

          setFieldValues({
            ...defaultValue,
            hasLanguageProficiency: hasLanguageProficiency,
            testType: dialogData.newTestValue || '',
          })

          setDialogData({
            currentTestValue: '',
            isOpen: false,
            newTestValue: null,
          })
        }}
      />
    </>
  )
}

type SelectedDuolingoTypeFieldsProps = {
  readonly disabled?: boolean
}

function SelectedDuolingoTypeFields(props: SelectedDuolingoTypeFieldsProps) {
  const { hasLanguageProficiency, testType, testDate } = useFieldValues([
    'hasLanguageProficiency',
    'testType',
    'testDate',
  ])
  const setFieldValues = useSetFieldValues()

  // Duolingo is giving us individual subscores starting July 1st, 2024
  const DUOLINGO_REFERENCE_DATE_TO_ADD_SUBSCORES = new Date('2024-07-01')
  const isBeforeReferenceDate =
    testDate && new Date(testDate) < DUOLINGO_REFERENCE_DATE_TO_ADD_SUBSCORES

  useEffect(() => {
    if (isBeforeReferenceDate) {
      setFieldValues({
        listening: '',
        reading: '',
        writing: '',
        speaking: '',
      })
    }
  }, [isBeforeReferenceDate])

  const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)
  if (!fieldsInfo) {
    return null
  }

  if (testType !== 'DUOLINGO') {
    return null
  }

  const subscoreOrder = getSubscoresOrder(testType)
  const integratedSubcoreOrder = getSubscoresOrder('DUOLINGO_INTEGRATED')

  const maxDate = new Date()
  const minDate = new Date()

  minDate.setFullYear(minDate.getFullYear() - 50)

  const datePickerValidator = createDatePickerValidator(minDate, maxDate, 'day')
  return (
    <>
      <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={DatePicker}
            label="Test date"
            name={`testDate`}
            maxDate={maxDate.toISOString()}
            minDate={minDate.toISOString()}
            validate={datePickerValidator}
            disabled={props.disabled}
            required={
              isFieldRequired({
                testType,
                disabled: props.disabled,
                hasLanguageProficiency: hasLanguageProficiency,
              })
                ? 'Test date is required'
                : false
            }
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={TextInput as ComponentType<TextInputProps>}
            label={fieldsInfo.codeLabel}
            name={`certificateNumber`}
            disabled={props.disabled}
            required={
              isFieldRequired({
                testType,
                disabled: props.disabled,
                hasLanguageProficiency: hasLanguageProficiency,
              })
                ? `${fieldsInfo.codeLabel} is required`
                : false
            }
          />
        </Flex.Item>
      </Flex>
      {testDate ? (
        <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            <Field
              as={NumberInput as ComponentType<NumberInputProps>}
              label="Overall score"
              name={`overallScore`}
              max={fieldsInfo.maxOverall}
              min={fieldsInfo.minOverall}
              required={
                isFieldRequired({
                  testType,
                  disabled: props.disabled,
                  hasLanguageProficiency: hasLanguageProficiency,
                })
                  ? 'Overall score is required'
                  : false
              }
              validate={data => {
                if (
                  parseInt(data) < (fieldsInfo.minOverall || 0) ||
                  parseInt(data) > (fieldsInfo.maxOverall || 0)
                ) {
                  return 'Invalid score'
                }

                return true
              }}
              disabled={props.disabled}
            />
          </Flex.Item>
        </Flex>
      ) : null}
      {!isBeforeReferenceDate && testDate ? (
        <>
          <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Text variant="labelM">Individual Subscores</Text>
            </Flex.Item>
          </Flex>
          <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            {subscoreOrder.map(subscore => (
              <SubscoresField
                key={`${subscore.name}`}
                label={subscore.label}
                name={subscore.name}
                disabled={props.disabled}
              />
            ))}
          </Flex>
        </>
      ) : null}
      {testDate ? (
        <>
          <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Text variant="labelM">Integrated Subscores</Text>
            </Flex.Item>
          </Flex>
          <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            {integratedSubcoreOrder.map(subscore => (
              <SubscoresField
                key={`${subscore.name}`}
                label={subscore.label}
                name={subscore.name}
                disabled={props.disabled}
              />
            ))}
          </Flex>
        </>
      ) : null}
    </>
  )
}

type SelectedOtherTypeFieldsProps = {
  readonly disabled?: boolean
}

function SelectedOtherTypeFields(props: SelectedOtherTypeFieldsProps) {
  const { testType } = useFieldValues(['testType'])
  if (testType !== 'OTHER') {
    return null
  }
  return (
    <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
      <Flex.Item basis={{ xs: '100%' }}>
        <Field
          as={TextInput as ComponentType<TextInputProps>}
          label="Please specify other proof type"
          name={`description`}
          disabled={props.disabled}
          helpText="Provide a description with up to 200 characters"
          validate={value => {
            if (value.length > 200) {
              return 'This field has a character limit of 200'
            }
            return true
          }}
          required={'This field is required'}
        />
      </Flex.Item>
    </Flex>
  )
}

type SelectedTypeFieldsProps = {
  disabled?: boolean
  application: StudentApplication
}

function SelectedTypeFields(props: SelectedTypeFieldsProps) {
  const { hasLanguageProficiency, testType } = useFieldValues([
    'hasLanguageProficiency',
    'testType',
  ])

  if (includes(['CAEL', 'MET', 'CAMBRIDGE', 'EPP', 'DUOLINGO', 'OTHER'], testType)) {
    return null
  }

  const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)
  if (!fieldsInfo) {
    return null
  }

  const subscoreOrder = getSubscoresOrder(testType)

  const maxDate = new Date()
  const minDate = new Date()

  minDate.setFullYear(minDate.getFullYear() - 50)

  const datePickerValidator = createDatePickerValidator(minDate, maxDate, 'day')

  return (
    <>
      <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={DatePicker}
            label="Test date"
            name={`testDate`}
            maxDate={maxDate.toISOString()}
            minDate={minDate.toISOString()}
            validate={datePickerValidator}
            disabled={props.disabled}
            required={
              isFieldRequired({
                testType,
                disabled: props.disabled,
                hasLanguageProficiency: hasLanguageProficiency,
              })
                ? 'Test date is required'
                : false
            }
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={TextInput as ComponentType<TextInputProps>}
            label={fieldsInfo.codeLabel}
            name={`certificateNumber`}
            disabled={props.disabled}
            required={
              isFieldRequired({
                testType,
                disabled: props.disabled,
                hasLanguageProficiency: hasLanguageProficiency,
              })
                ? `${fieldsInfo.codeLabel} is required`
                : false
            }
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={NumberInput as ComponentType<NumberInputProps>}
            label="Overall score"
            name={`overallScore`}
            max={fieldsInfo.maxOverall}
            min={fieldsInfo.minOverall}
            required={
              isFieldRequired({
                testType,
                disabled: props.disabled,
                hasLanguageProficiency: hasLanguageProficiency,
              })
                ? 'Overall score is required'
                : false
            }
            validate={data => {
              if (
                parseInt(data) < (fieldsInfo.minOverall || 0) ||
                parseInt(data) > (fieldsInfo.maxOverall || 0)
              ) {
                return 'Invalid score'
              }

              return true
            }}
            disabled={props.disabled}
          />
        </Flex.Item>
      </Flex>
      <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
        {subscoreOrder.map(subscore => (
          <SubscoresField
            key={`${subscore.name}`}
            label={subscore.label}
            name={subscore.name}
            disabled={props.disabled}
          />
        ))}
      </Flex>
    </>
  )
}

type UploadFieldProps = {
  readonly disabled?: boolean
  readonly application: StudentApplication
}

function UploadField(props: UploadFieldProps) {
  const { id, testType, documents, testDate } = useFieldValues([
    'id',
    'testType',
    'documents',
    'testDate',
  ])
  const setFieldValues = useSetFieldValues()
  const { getObservableFiles } = useApplicationFormContext()
  const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)
  if (testType === 'DUOLINGO' && !testDate) {
    return null
  }
  if (!fieldsInfo) {
    return null
  }
  return (
    <Field
      as={FileUploadField}
      allowedFileTypes={['.jpg', '.pdf', '.png', '.jpeg']}
      application={props.application}
      disabled={props.disabled}
      fileLimit={UPLOAD_LIMIT}
      fileType={fieldsInfo.fileType}
      label={
        <Text variant={'bodyM'}>
          Add your language proficiency document below, supported file formats: JPG, JPEG, PDF, PNG,
          max number of files: {UPLOAD_LIMIT} <Asterisk />
        </Text>
      }
      name={`documents`}
      onRemove={(id: string) =>
        setFieldValues({ documents: documents.filter(file => file.id !== id) })
      }
      section={id}
      showHistory={!!props.disabled}
      validate={value => {
        const observableFiles = getObservableFiles({
          fileType: fieldsInfo.fileType,
          sectionReference: id,
        })
        if (Object.keys(observableFiles).length > UPLOAD_LIMIT) {
          return `This field has a file limit of ${UPLOAD_LIMIT}.`
        }
        if (!(Object.keys(observableFiles).length || value.length)) {
          return 'This field is required'
        }
        return true
      }}
    />
  )
}

type SubscoresFieldProps = {
  label: string
  name: Subscores | IntegratedSubscores
  disabled?: boolean
}

function SubscoresField(props: SubscoresFieldProps) {
  const { hasLanguageProficiency, testType } = useFieldValues([
    'hasLanguageProficiency',
    'testType',
  ])
  const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)
  if (!fieldsInfo) return null

  return (
    <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' }}>
      <Field
        as={NumberInput as ComponentType<NumberInputProps>}
        label={props.label}
        name={`${props.name}`}
        max={fieldsInfo.maxSubscore}
        min={fieldsInfo.minSubscore}
        step={fieldsInfo.step}
        required={
          isFieldRequired({
            testType,
            disabled: props.disabled,
            hasLanguageProficiency: hasLanguageProficiency,
          })
            ? `${props.label} score is required`
            : false
        }
        validate={data => {
          return validateSubscore(testType, parseInt(typeof data === 'string' ? data : '0'))
        }}
        disabled={props.disabled}
      />
    </Flex.Item>
  )
}

function setDefaultLanguageProficiency({
  languageProficiency,
  files,
}: {
  languageProficiency?: SetAllFieldsOptional<LanguageProficiency>
  files: FileData
}) {
  let formData = {
    hasLanguageProficiency: languageProficiency?.hasLanguageProficiency || '',
    ...defaultValue,
    id: nanoid(),
  }
  if (
    !languageProficiency?.languageProficiencyData ||
    !Object.keys(languageProficiency.languageProficiencyData).length
  )
    return formData

  const activeDocument = find(
    files,
    doc =>
      ((doc?.type as unknown as DocumentTags | undefined) === DocumentTags.LANGUAGE_TEST_IELTS ||
        (doc?.type as unknown as DocumentTags | undefined) === DocumentTags.LANGUAGE_TEST_PTE ||
        (doc?.type as unknown as DocumentTags | undefined) === DocumentTags.LANGUAGE_TEST_OTHER ||
        (doc?.type as unknown as DocumentTags | undefined) ===
          DocumentTags.LANGUAGE_TEST_DUOLINGO ||
        (doc?.type as unknown as DocumentTags | undefined) === DocumentTags.LANGUAGE_TEST_CAEL ||
        (doc?.type as unknown as DocumentTags | undefined) === DocumentTags.LANGUAGE_TEST_MET ||
        (doc?.type as unknown as DocumentTags | undefined) ===
          DocumentTags.LANGUAGE_TEST_CAMBRIDGE ||
        (doc?.type as unknown as DocumentTags | undefined) === DocumentTags.LANGUAGE_TEST_EPP ||
        (doc?.type as unknown as DocumentTags | undefined) === DocumentTags.LANGUAGE_TEST_TOEFL) &&
      !!doc?.activeRecord,
  )

  const id = activeDocument?.sectionReference || ''

  const exam = languageProficiency.languageProficiencyData[id]
  formData = {
    hasLanguageProficiency: languageProficiency?.hasLanguageProficiency || '',
    testType: exam?.testType || '',
    certificateNumber: exam?.certificateNumber || '',
    documents: [],
    testDate: exam?.testDate ? convertTimelessDateStrToLocalDate(exam.testDate).toISOString() : '',
    overallScore: exam?.overallScore || '',
    listening: exam?.subscores?.listening?.toString() || '',
    reading: exam?.subscores?.reading?.toString() || '',
    writing: exam?.subscores?.writing?.toString() || '',
    speaking: exam?.subscores?.speaking?.toString() || '',
    production: exam?.integratedSubscores?.production?.toString() || '',
    literacy: exam?.integratedSubscores?.literacy?.toString() || '',
    comprehension: exam?.integratedSubscores?.comprehension?.toString() || '',
    conversation: exam?.integratedSubscores?.conversation?.toString() || '',
    description: exam?.description || '',
    id: id,
  }

  return formData
}

function isFieldRequired(params: {
  testType: string
  hasLanguageProficiency?: string
  disabled?: boolean
}) {
  if (params.disabled || params.hasLanguageProficiency !== LanguageExamAvailability.HAVE) {
    return false
  }

  return true
}
